import { useState, type FormEvent } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { aboutInfo } from '@/mocks/articles';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d9q9q4me3oq4jqumtin0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(
          Array.from(formData.entries())
            .filter(([key]) => key !== 'phone_alt')
            .map(([key, value]) => [key, value as string])
        ).toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // not json
      }

      if (response.ok && parsed && (parsed as { code?: string }).code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg =
          (parsed as { meta?: { message?: string } })?.meta?.message ||
          (parsed as { message?: string })?.message ||
          (parsed as { meta?: { detail?: string } })?.meta?.detail ||
          responseText ||
          '提交失败，请稍后重试。';
        setFormError(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后重试。');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查您的网络连接后重试。');
      setFormStatus('error');
    }
  };

  return (
    <>
      <Navbar />
      <div className="pt-16">
        <Breadcrumb />
      </div>
      <main>
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-2">
              关于本站
            </h1>
            <p className="text-foreground-500 text-sm mb-10">
              了解郝海东官方介绍站的使命与宗旨
            </p>

            <div className="flex flex-col gap-10">
              <div className="bg-background-100 rounded-xl p-6 md:p-8 border border-background-200/70">
                <h2 className="font-heading text-xl font-bold text-foreground-900 mb-4 flex items-center gap-2">
                  <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                    <i className="ri-information-line"></i>
                  </span>
                  品牌介绍
                </h2>
                <p className="text-foreground-700 leading-relaxed mb-4">
                  {aboutInfo.intro}
                </p>
                <p className="text-foreground-700 leading-relaxed">
                  {aboutInfo.mission}
                </p>
              </div>

              <div className="bg-background-100 rounded-xl p-6 md:p-8 border border-background-200/70">
                <h2 className="font-heading text-xl font-bold text-foreground-900 mb-4 flex items-center gap-2">
                  <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100 text-accent-700">
                    <i className="ri-shield-check-line"></i>
                  </span>
                  声誉声明
                </h2>
                <p className="text-foreground-700 leading-relaxed">
                  {aboutInfo.reputation}
                </p>
              </div>

              <div className="bg-background-100 rounded-xl p-6 md:p-8 border border-background-200/70">
                <h2 className="font-heading text-xl font-bold text-foreground-900 mb-4 flex items-center gap-2">
                  <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-700">
                    <i className="ri-mail-line"></i>
                  </span>
                  联系我们
                </h2>
                <p className="text-foreground-600 text-sm mb-6">
                  如果您有任何关于郝海东的资料、建议或合作意向，欢迎通过以下表单与我们联系。
                </p>

                <form
                  id="contact-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="flex flex-col gap-5"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div className="flex flex-col gap-1.5">
                      <label htmlFor="contact-name" className="text-sm font-medium text-foreground-800">
                        姓名
                      </label>
                      <input
                        id="contact-name"
                        type="text"
                        name="name"
                        required
                        placeholder="请输入您的姓名"
                        className="px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                      />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label htmlFor="contact-email" className="text-sm font-medium text-foreground-800">
                        邮箱
                      </label>
                      <input
                        id="contact-email"
                        type="email"
                        name="email"
                        required
                        placeholder="请输入您的邮箱地址"
                        className="px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                      />
                    </div>
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="contact-subject" className="text-sm font-medium text-foreground-800">
                      主题
                    </label>
                    <select
                      id="contact-subject"
                      name="subject"
                      required
                      defaultValue=""
                      className="px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all appearance-none"
                    >
                      <option value="" disabled>请选择主题</option>
                      <option value="一般咨询">一般咨询</option>
                      <option value="资料提供">资料提供</option>
                      <option value="合作意向">合作意向</option>
                      <option value="其他">其他</option>
                    </select>
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="contact-message" className="text-sm font-medium text-foreground-800">
                      留言内容
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      rows={5}
                      maxLength={500}
                      placeholder="请输入您的留言内容（不超过500字）"
                      className="px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                    ></textarea>
                    <p className="text-xs text-foreground-400 text-right">最多500字</p>
                  </div>

                  <div className="honeypot-wrapper">
                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                  </div>

                  <div className="flex flex-col sm:flex-row items-center gap-4">
                    <button
                      type="submit"
                      disabled={formStatus === 'submitting'}
                      className="px-8 py-3 bg-primary-500 text-background-50 rounded-lg text-sm font-semibold hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-200 whitespace-nowrap"
                    >
                      {formStatus === 'submitting' ? (
                        <span className="flex items-center gap-2">
                          <i className="ri-loader-4-line animate-spin"></i>
                          提交中...
                        </span>
                      ) : (
                        '提交留言'
                      )}
                    </button>
                  </div>

                  {formStatus === 'success' && (
                    <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-accent-100 text-accent-800 text-sm">
                      <i className="ri-check-line"></i>
                      感谢您的留言！我们会尽快回复。
                    </div>
                  )}

                  {formStatus === 'error' && (
                    <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-primary-50 text-primary-700 text-sm">
                      <i className="ri-error-warning-line"></i>
                      {formError || '提交失败，请稍后重试。'}
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}